import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { fadeInRightAnimation } from '../../../@fury/animations/fade-in-right.animation';
import { scaleInAnimation } from '../../../@fury/animations/scale-in.animation';
import { fadeInUpAnimation } from '../../../@fury/animations/fade-in-up.animation';
import { Subject } from 'rxjs';
import { FiltersService } from '../../services/base/filters.service';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { HeadCategory } from '../../interfaces/routering/head-category';
import { SubCategory } from '../../interfaces/routering/sub-category';
import * as moment from 'moment';
import { IntegratedOptions } from '../../interfaces/base/integrated-options';
import { NationaalGeoregisterSuggest } from '../../interfaces/external-services/nationaal-georegister-suggest';
import { PdokService } from '../../services/external/pdok.service';
import { LeafletMapService } from '../../services/base/leaflet-map.service';
import { NationaalGeoregisterRevGeoServerDocs } from '../../interfaces/external-services/nationaal-georegister-rev-geo';
import { NationaalGeoregisterLookupServerDocs } from '../../interfaces/external-services/nationaal-georegister-lookup';
import { WebformService } from '../../services/reports/webform.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RemovableAttachmentComponent } from '../../../@fury/shared/removable-attachment/removable-attachment.component';
import { LayoutService } from '../../layout/layout.service';
import { AuthenticationService } from '../../services/base/authentication.service';
import { Organisation } from '../../interfaces/routering/organisation';
import { Title } from '@angular/platform-browser';
import * as _ from 'lodash';
import { Domain } from '../../interfaces/routering/domain';
import { Module } from '../../interfaces/configuration/module';

@Component({
  selector: 'fury-normal-form',
  templateUrl: './normal-form.component.html',
  styleUrls: ['./normal-form.component.scss'],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation]
})
export class NormalFormComponent implements OnInit, OnDestroy, AfterViewInit {

  protected readonly Validators = Validators;

  public filters = {
    'headCategories': {
      preload: true,
      isLoading: false,
      data: <HeadCategory[]>[]
    },
    'subCategories': {
      preload: false,
      isLoading: false,
      data: <SubCategory[]>[]
    },
    'personPrefix': {
      preload: true,
      isLoading: false,
      data: <IntegratedOptions[]>[]
    },
  };
  public currentSection: string = 'melding';
  public controlForm: boolean = false;
  public selectedHeadCategoryNote: string = null;
  public selectedSubCategoryNote: string = null;
  @ViewChild('attachmentsPlaceholder', {read: ViewContainerRef}) attachmentsPlaceholder: ViewContainerRef;
  public attachmentsRef: Array<ComponentRef<RemovableAttachmentComponent>> = [];
  public shouldGeocodeReportLocation: boolean = true;
  public suggestReportLocations: any = null;
  public isLoadingReportLocations: boolean = false;
  public isLoadingReporterLocations: boolean = false;

  public isLoading: boolean = null;

  public reportsMapUrl: string = null;

  public userLocationError: boolean = false;

  public attachmentIgnored: string = null;
  public attachmentCountExceeded: boolean = false;
  public attachmentTotalSizeExceeded: boolean = false;

  public acceptedMaxAttachments: number = 6;
  public acceptedMaxSizeAttachments: number = 31457280;
  private acceptedAttachments: string[] = ['jpg', 'jpeg', 'png', 'pdf', 'mp4', 'flv', 'doc', 'docx', 'xlsx', 'xls'];

  private pageTitle: string = 'Melding openbare ruimte';

  private blueTimeout: NodeJS.Timeout = null;
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private filtersService: FiltersService,
              private pdokService: PdokService,
              private cd: ChangeDetectorRef,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private layoutService: LayoutService,
              private titleService: Title,
              public authenticationService: AuthenticationService,
              public webformService: WebformService,
              public leafletMapService: LeafletMapService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle + ' - Straatbeeld Online');
    this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
      if (organisation && typeof organisation.display_name !== 'undefined') {
        this.titleService.setTitle(this.pageTitle + ' - ' + organisation.display_name);

        if (organisation.modules.find((d: Module): boolean => d.module.name === 'reports-map') !== undefined) {
          const webformUrl: Domain = organisation.domains.find((d: Domain): boolean => d.type.name === 'reports-map');

          if (webformUrl !== undefined) {
            this.reportsMapUrl = webformUrl.url;
          }
        }
      }
    });

    this.filtersService.filters = this.filters;
    this.layoutService.isPublicNotificationVisible.emit(true);

    this.leafletMapService.mapReady.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      if (value) {
        this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
          if (!this.webformService.normalForm.controls.report_lat.value) {
            this.leafletMapService.setInitialView(organisation);
          }
        });
      }
    });

    this.webformService.normalForm.controls.form_filling_start_at.setValue(moment().toISOString());

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          if (data) {
            this.filters[key].data = data;

            if (key === 'headCategories') {
              this.activatedRoute.queryParams.pipe(take(1)).subscribe((params: Params): void => {
                if (typeof params.category !== 'undefined') {
                  data.forEach((category: HeadCategory): void => {
                    if (category.name === params.category) {
                      this.webformService.normalForm.controls.head_category_id.setValue(category.id);
                    }
                  });
                }
              });
            }
          }
        });
      }
    });

    this.webformService.normalForm.controls.head_category_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      const id: number = (value ? parseInt(value, 10) : null);

      this.webformService.normalForm.controls.sub_category_id.setValue(null);
      this.filters.subCategories.data = [];

      this.selectedHeadCategoryNote = null;
      if (id) {
        const headCategory: HeadCategory = this.filters.headCategories.data.find((d: HeadCategory): boolean => d.id === id);
        if (typeof headCategory !== 'undefined') {
          if (headCategory.description !== null) {
            this.selectedHeadCategoryNote = headCategory.description;
          }

          if (headCategory.sub_categories_count) {
            this.webformService.normalForm.controls.sub_category_id.addValidators(Validators.required);
            this.filters.subCategories.isLoading = true;
            this.filtersService.subCategories(id).pipe(takeUntil(this.destroy$)).subscribe((response: SubCategory[] | false): void => {
              this.filters.subCategories.isLoading = false;

              if (response) {
                this.filters.subCategories.data = response;
              }
            });
          } else {
            this.webformService.normalForm.controls.sub_category_id.removeValidators(Validators.required);
          }
        } else {
          this.webformService.normalForm.controls.sub_category_id.removeValidators(Validators.required);
        }
      } else {
        this.webformService.normalForm.controls.sub_category_id.removeValidators(Validators.required);
      }
    });

    this.webformService.normalForm.controls.sub_category_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      const id: number = (value ? parseInt(value, 10) : null);

      this.selectedSubCategoryNote = null;
      if (id) {
        const subCategory: SubCategory = this.filters.subCategories.data.find((d: SubCategory): boolean => d.id === id);
        if (typeof subCategory !== 'undefined') {
          if (subCategory.description !== null) {
            this.selectedSubCategoryNote = subCategory.description;
          }
        }
      }
    });

    this.webformService.normalForm.controls.report_location.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      filter((query: string): boolean | null => query && (typeof query === 'object' || typeof query === 'string' && (query.length > 2 ? query.length > 2 : null))),
      takeUntil(this.destroy$)
    ).subscribe((value: string | any): void => {
      this.suggestReportLocations = [];

      this.webformService.normalForm.controls.report_postalcode.setValue(null);
      this.webformService.normalForm.controls.report_house_number_with_addition.setValue(null);
      this.webformService.normalForm.controls.report_streetname.setValue(null);
      this.webformService.normalForm.controls.report_city.setValue(null);
      this.webformService.normalForm.controls.report_lat.setValue(null);
      this.webformService.normalForm.controls.report_lng.setValue(null);

      if (value) {
        if (typeof value === 'string') {
          this.isLoadingReportLocations = true;

          this.pdokService.getSearchedAddress(value).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterSuggest): void => {
              this.isLoadingReportLocations = false;

              if (response) {
                if (response.response.numFound > 0) {
                  this.suggestReportLocations = response.response.docs;
                } else {
                  this.suggestReportLocations = [{
                    id: null,
                    weergavenaam: 'Er zijn geen resultaten gevonden, probeer een ander adres.'
                  }];
                }
              }
            },
            (): boolean => this.isLoadingReportLocations = false);
        } else {
          this.pdokService.selectSearchedAddress(value.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
            this.webformService.normalForm.controls.report_location.setValue(response.weergavenaam, {emitEvent: false});
            this.webformService.normalForm.controls.report_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
            this.webformService.normalForm.controls.report_house_number_with_addition.setValue((typeof response.huisnummer !== 'undefined' ? response.huisnummer + (typeof response.huisletter !== 'undefined' ? response.huisletter : null) : null));
            this.webformService.normalForm.controls.report_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null);
            this.webformService.normalForm.controls.report_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
            this.webformService.normalForm.controls.report_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null);
            this.webformService.normalForm.controls.report_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null);

            this.leafletMapService.updateMarker([response.lat, response.lng]);
          });
        }
      }
    });

    this.webformService.normalForm.controls.reporter_anonymous.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      const emailElement = this.webformService.normalForm.controls.reporter_email,
        lastnameElement = this.webformService.normalForm.controls.reporter_lastname;
      if (value) {
        emailElement.removeValidators(Validators.required);
        lastnameElement.removeValidators(Validators.required);

        this.webformService.normalForm.controls.reporter_prefix.setValue('Neutraal', {emitEvent: false});
        this.webformService.normalForm.controls.reporter_initials.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_lastname.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_location.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_postalcode.setValue(null);
        this.webformService.normalForm.controls.reporter_house_number_with_addition.setValue(null);
        this.webformService.normalForm.controls.reporter_streetname.setValue(null);
        this.webformService.normalForm.controls.reporter_city.setValue(null);
        this.webformService.normalForm.controls.reporter_lat.setValue(null);
        this.webformService.normalForm.controls.reporter_lng.setValue(null);
        this.webformService.normalForm.controls.reporter_email.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_phone.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_notify.setValue(true, {emitEvent: false});
      } else {
        emailElement.addValidators(Validators.required);
        lastnameElement.addValidators(Validators.required);
      }
      emailElement.updateValueAndValidity();
      lastnameElement.updateValueAndValidity();
    });

    this.webformService.normalForm.controls.reporter_initials.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      this.webformService.normalForm.controls.reporter_initials.setValue([...value.toUpperCase().replace(/\./g, '')].join('.') + '.', {emitEvent: false});
    });

    this.webformService.normalForm.controls.reporter_notify.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      const element: FormControl = this.webformService.normalForm.controls.reporter_email;
      if (value) {
        element.addValidators(Validators.required);
      } else {
        element.removeValidators(Validators.required);
      }
      element.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    if (this.webformService.normalForm.controls.attachments.value.length) {
      const formArray: FormArray = this.webformService.normalForm.get('attachments') as FormArray;

      this.webformService.normalForm.controls.attachments.value.forEach((file: File): void => {
        const component: ComponentRef<RemovableAttachmentComponent> = this.attachmentsPlaceholder.createComponent(RemovableAttachmentComponent);
        component.instance.filename = file.name;
        component.instance.size = file.size;

        this.attachmentsRef.push(component);

        component.instance.destroy.subscribe((): void => {
          const index: number = formArray.controls.findIndex(x => x.value.name === file.name);
          formArray.removeAt(index);
          component.destroy();
        });
      });
    }

    if (this.webformService.normalForm.controls.head_category_id.value && this.webformService.normalForm.controls.sub_category_id.value) {
      this.filtersService.subCategories(this.webformService.normalForm.controls.head_category_id.value).pipe(takeUntil(this.destroy$)).subscribe((response: SubCategory[] | false): void => {
        this.filters.subCategories.isLoading = false;
        if (response) {
          this.filters.subCategories.data = response;
        }
      });
    }

    if (this.webformService.normalForm.controls.report_lat.value && this.webformService.normalForm.controls.report_lng.value) {
      this.leafletMapService.mapReady.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
        if (value) {
          this.leafletMapService.updateMarker([
            <number>this.webformService.normalForm.controls.report_lat.value,
            <number>this.webformService.normalForm.controls.report_lng.value
          ]);
        }
      });
    }

    if (!_.isEmpty(this.webformService.normalFormErrors)) {
      Object.entries(this.webformService.normalFormErrors).forEach(([field, messages]: [string, string[]]): void => {
        const control = this.webformService.normalForm.get(field);

        if (control) {
          control.setErrors({
            server: messages[0]
          });
        }
      });
    }
  }

  onSectionChange(section: string): void {
    this.currentSection = section ?? 'melding';
    this.cd.detectChanges();
  }

  detectUploadAttachments(event: any): void {
    if (typeof event !== 'undefined') {
      if (typeof event.target !== 'undefined') {
        if (typeof event.target.files !== 'undefined') {
          const formArray: FormArray = this.webformService.normalForm.get('attachments') as FormArray;
          let totalSize: number = 0;

          Object.keys(this.webformService.normalForm.controls.attachments.value).map((d: string): void => {
            totalSize += this.webformService.normalForm.controls.attachments.value[d].size;
          });

          Object.keys(event.target.files).map((file: string): void => {
            if (this.webformService.normalForm.controls.attachments.length < this.acceptedMaxAttachments) {
              if (this.acceptedAttachments.includes(event.target.files[file].name.split('.').pop().toLowerCase())) {
                if ((totalSize += event.target.files[file].size) < this.acceptedMaxSizeAttachments) {
                  const component: ComponentRef<RemovableAttachmentComponent> = this.attachmentsPlaceholder.createComponent(RemovableAttachmentComponent);
                  component.instance.filename = event.target.files[file].name;
                  component.instance.size = event.target.files[file].size;

                  this.attachmentsRef.push(component);

                  const control: FormControl<File> = new FormControl(event.target.files[file]);
                  formArray.push(control);
                  component.instance.control = control;

                  component.instance.destroy.subscribe((): void => {
                    formArray.removeAt(formArray.controls.indexOf(component.instance.control));
                    component.destroy();
                  });
                } else {
                  this.attachmentTotalSizeExceeded = true;
                  setTimeout((): boolean => this.attachmentTotalSizeExceeded = false, 10000);
                }
              } else {
                this.attachmentIgnored = event.target.files[file].name;
                setTimeout((): void => this.attachmentIgnored = null, 10000);
              }
            } else {
              this.attachmentCountExceeded = true;
              setTimeout((): boolean => this.attachmentCountExceeded = false, 10000);
            }
          });
        }
      }
    }
  }

  formatLocationDropdown(result: any): string {
    return result !== null && typeof result === 'object' ? result.weergavenaam : result;
  }

  onMapClick(event: any): void {
    this.webformService.normalForm.controls.report_location.setValue(null);

    this.pdokService.doReverseSearch([event.latlng.lat, event.latlng.lng]).pipe(takeUntil(this.destroy$)).subscribe((result: NationaalGeoregisterRevGeoServerDocs): void => {
      if (result) {
        if (this.shouldGeocodeReportLocation === true) {
          this.pdokService.selectSearchedAddress(result.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
            this.webformService.normalForm.controls.report_location.setValue(response.weergavenaam, {emitEvent: false});
            this.webformService.normalForm.controls.report_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
            this.webformService.normalForm.controls.report_house_number_with_addition.setValue(typeof response.huisnummer !== 'undefined' ? response.huisnummer + (typeof response.huisletter !== 'undefined' ? response.huisletter : null) : null);
            this.webformService.normalForm.controls.report_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null);
            this.webformService.normalForm.controls.report_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
//            this.webformService.normalForm.controls.report_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null);
//            this.webformService.normalForm.controls.report_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null);

//            this.leafletMapService.updateMarker([response.lat, response.lng]);

            this.webformService.normalForm.controls.report_lat.setValue(event.latlng.lat);
            this.webformService.normalForm.controls.report_lng.setValue(event.latlng.lng);
            this.leafletMapService.updateMarker([event.latlng.lat, event.latlng.lng]);
          });
        } else {
          this.pdokService.selectSearchedAddress(result.id).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
            this.webformService.normalForm.controls.report_location.setValue(event.latlng.lat + ', ' + event.latlng.lng, {emitEvent: false});
            this.webformService.normalForm.controls.report_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null);
            this.webformService.normalForm.controls.report_house_number_with_addition.setValue(null);
            this.webformService.normalForm.controls.report_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null);
//            this.webformService.normalForm.controls.report_lat.setValue(event.latlng.lat);
//            this.webformService.normalForm.controls.report_lng.setValue(event.latlng.lng);

//            this.leafletMapService.updateMarker([event.latlng.lat, event.latlng.lng]);

            this.webformService.normalForm.controls.report_lat.setValue(event.latlng.lat);
            this.webformService.normalForm.controls.report_lng.setValue(event.latlng.lng);
            this.leafletMapService.updateMarker([event.latlng.lat, event.latlng.lng]);
          });
        }
      }
    });
  }

  updateReporterAddress(): void {
    if (this.blueTimeout) {
      clearTimeout(this.blueTimeout);
    }

    this.blueTimeout = setTimeout(() => {
      const postalCode: string = this.webformService.normalForm.controls.reporter_postalcode.value?.replace(/\s+/g, '')?.trim(),
        houseNumber: string = this.webformService.normalForm.controls.reporter_house_number_with_addition.value?.replace(/\s+/g, '')?.trim();

      this.webformService.normalForm.controls.reporter_location.setErrors(null);

      if (!postalCode || !houseNumber) {
        this.webformService.normalForm.controls.reporter_streetname.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_city.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_lat.setValue(null, {emitEvent: false});
        this.webformService.normalForm.controls.reporter_lng.setValue(null, {emitEvent: false});
        return null;
      }

      this.pdokService.getAddress(postalCode, houseNumber).pipe(takeUntil(this.destroy$)).subscribe((response: NationaalGeoregisterLookupServerDocs): void => {
          this.isLoadingReporterLocations = false;
          if (response) {
            this.webformService.normalForm.controls.reporter_location.setValue(response.weergavenaam, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_postalcode.setValue(typeof response.postcode !== 'undefined' ? response.postcode : null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_house_number_with_addition.setValue((typeof response.huisnummer !== 'undefined' ? response.huisnummer + (typeof response.huisletter !== 'undefined' ? response.huisletter : null) + '' : null), {emitEvent: false});
            this.webformService.normalForm.controls.reporter_streetname.setValue(typeof response.straatnaam !== 'undefined' ? response.straatnaam : null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_city.setValue(typeof response.woonplaatsnaam !== 'undefined' ? response.woonplaatsnaam : null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_lat.setValue(typeof response.lat !== 'undefined' ? response.lat : null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_lng.setValue(typeof response.lng !== 'undefined' ? response.lng : null, {emitEvent: false});
          } else {
            this.webformService.normalForm.controls.reporter_location.setErrors({invalid: true});
            this.webformService.normalForm.controls.reporter_streetname.setValue(null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_city.setValue(null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_lat.setValue(null, {emitEvent: false});
            this.webformService.normalForm.controls.reporter_lng.setValue(null, {emitEvent: false});
          }
        },
        (): boolean => this.isLoadingReporterLocations = false);
    }, 300);
  }

  validateReportLocation(): void {
    const controls = this.webformService.normalForm.controls;
    if (controls.report_location.value) {
      if (controls.report_lat.value === null) {
        controls.report_location.setErrors({invalid: true});
      } else {
        controls.report_location.setErrors(null);
      }
    }
  }

  isHeadCategoryAnExternalLink(): boolean {
    const headCategory: HeadCategory = this.filters.headCategories.data.find((d: HeadCategory): boolean => d.id === parseInt(this.webformService.normalForm.controls.head_category_id.value, 10));
    if (typeof headCategory !== 'undefined') {
      if (typeof headCategory.external_link !== 'undefined') {
        return headCategory.external_link;
      }
    }

    return false;
  }

  isSubCategoryAnExternalLink(): boolean {
    const subCategory: SubCategory = this.filters.subCategories.data.find((d: SubCategory): boolean => d.id === parseInt(this.webformService.normalForm.controls.sub_category_id.value, 10));
    if (typeof subCategory !== 'undefined') {
      if (typeof subCategory.external_link !== 'undefined') {
        return subCategory.external_link;
      }
    }

    return false;
  }

  checkForm(): void {
    this.controlForm = true;
    if (this.webformService.normalForm.valid) {
      this.webformService.checkForm = true;
      this.router.navigate(['/controle']).then((): void => {
      });
    }
  }

  getUserLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: GeolocationPosition): void => {
        this.onMapClick({
          latlng: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
        });
      }, (): void => {
        this.userLocationError = true;
        setTimeout((): boolean => this.userLocationError = false, 5000);
      });
    } else {
      this.userLocationError = true;
      setTimeout((): boolean => this.userLocationError = false, 5000);
    }
  }

  get isLocal(): boolean {
    return window.location.href.indexOf('localhost') !== -1;
  }

  log(): void {
    console.log(this.webformService.normalForm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.blueTimeout) {
      clearTimeout(this.blueTimeout);
    }
  }
}
